package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceProgressResponse
import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.shared.invoices.InvoiceTransitionRequest
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.HTTP_FORBIDDEN
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

// `target` is the next state awaiting confirmation (step 7); `date` and `reason` are its inputs.
data class InvoiceProgressUiState(
    val progress: InvoiceProgressResponse? = null,
    val target: String? = null,
    val date: String = "",
    val reason: String = "",
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val readiness: List<String> = emptyList(),
    val notice: String? = null,
)

data class InvoiceProgressActions(
    val refresh: () -> Unit,
    val select: (String) -> Unit,
    val changeDate: (String) -> Unit,
    val changeReason: (String) -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
)

/**
 * UC-014 progression of one invoice. Single-flight; a disposed page scope cannot publish late
 * answers, so a change of organisation or invoice never shows another tenant's state. Only the next
 * states the server returned can be selected, and every confirmation carries the state the member
 * saw (B5). The latest invoice reaches the page through [changed]; a tenant denial clears the
 * invoice itself through [accessLost] (A11).
 */
class InvoiceProgressState(
    private val port: InvoiceProgressPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val invoiceId: String,
    private val changed: (InvoiceResponse) -> Unit,
    private val accessLost: (OnboardingProblem) -> Unit,
) {
    var state by mutableStateOf(InvoiceProgressUiState())
        private set

    val actions =
        InvoiceProgressActions(
            refresh = ::refresh,
            select = ::select,
            changeDate = { value -> if (open()) state = state.copy(date = value) },
            changeReason = { value -> if (open()) state = state.copy(reason = value) },
            confirm = ::confirm,
            // Cancelling sends nothing; the invoice keeps its state.
            cancel = {
                if (open())
                    state =
                        state.copy(
                            target = null,
                            problems = emptyMap(),
                            error = null,
                            notice = "Abgebrochen. Der Status der Rechnung wurde nicht geändert.",
                        )
            },
        )

    fun refresh() {
        request(recording = false) {
            val progress = port.progress(organisationId, invoiceId)
            currentCoroutineContext().ensureActive()
            show(progress)
            state = state.copy(progress = progress, target = null)
        }
    }

    // Steps 5 and 7, A1, A2, A4, A5: dates propose today in the organisation's time zone.
    private fun select(target: String) {
        val progress = state.progress ?: return
        if (state.busy || target !in progress.permittedTransitions) return
        state =
            InvoiceProgressUiState(
                progress = progress,
                target = target,
                date = progress.currentDate,
            )
    }

    // Steps 8-10.
    private fun confirm() {
        val progress = state.progress
        val target = state.target
        if (state.busy || progress == null || target == null) return
        val request =
            InvoiceTransitionRequest(
                expectedStatus = progress.invoice.status,
                targetStatus = target,
                date = state.date.takeIf { target == SENT || target == PAID },
                cancellationReason = state.reason.takeIf { target == CANCELLED },
            )
        request(recording = true) {
            val recorded = port.transition(organisationId, invoiceId, request)
            currentCoroutineContext().ensureActive()
            show(recorded)
            state =
                InvoiceProgressUiState(
                    progress = recorded,
                    busy = true,
                    notice = recordedNotice(recorded.invoice),
                )
        }
    }

    private fun open() = !state.busy && state.target != null

    private fun show(progress: InvoiceProgressResponse) {
        if (progress.invoice.id.equals(invoiceId, ignoreCase = true)) changed(progress.invoice)
    }

    private fun request(recording: Boolean, block: suspend () -> Unit) {
        if (state.busy) return
        state =
            state.copy(
                busy = true,
                error = null,
                problems = emptyMap(),
                readiness = emptyList(),
                notice = null,
            )
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: InvoiceRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure, recording)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // An unanswered confirmation leaves the recorded state unknown.
                state =
                    if (recording) state.copy(target = null, error = uncertainTransitionProblem())
                    else state.copy(error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: InvoiceRequestFailure, recording: Boolean) {
        val detail = failure.detail
        val latest = detail.progress
        latest?.let(::show)
        state =
            when {
                // A11: no invoice, state or history detail stays.
                OnboardingRequestFailure(failure.status, detail.code).deniesAccess() -> {
                    accessLost(
                        if (recording && failure.status == HTTP_FORBIDDEN) accessChangedProblem()
                        else failure.invoiceProblem(saving = false)
                    )
                    InvoiceProgressUiState(busy = true)
                }
                // A7, A10: the latest state and its permitted next states replace the shown ones.
                detail.code == "STATE_CHANGED" || detail.code == "TRANSITION_NOT_PERMITTED" -> {
                    val progress = latest ?: state.progress
                    InvoiceProgressUiState(
                        progress = progress,
                        busy = true,
                        error = progressRejection(detail.code, progress),
                    )
                }
                // A8: the invoice stays Draft without a number; every missing detail is listed.
                detail.code == "INVOICE_NOT_READY" ->
                    state.copy(
                        target = null,
                        readiness = detail.problems.map { readinessMessage(it.field, it.reason) },
                        error = notReadyProblem(),
                    )
                // A9.
                detail.code == "ISSUANCE_NOT_COMPLETED" ->
                    state.copy(target = null, error = issuanceNotCompletedProblem())
                // A6 and A5: the confirmation stays open with the rejected input marked.
                detail.code == "INVALID_DATE" || detail.code == "INVALID_CANCELLATION" -> {
                    val problems =
                        detail.problems.associate { it.field to transitionCorrection(it.reason) }
                    state.copy(
                        problems = problems,
                        error =
                            OnboardingProblem(
                                "Bitte korrigiere die markierten Angaben. Der Status der " +
                                    "Rechnung wurde nicht geändert.",
                                problems.keys.map(::invoiceFieldId),
                            ),
                    )
                }
                // A12: the confirmation stays open for another attempt.
                recording && failure.status >= HTTP_SERVER_ERROR ->
                    state.copy(error = transitionNotRecordedProblem())
                else -> state.copy(target = null, error = failure.invoiceProblem(saving = false))
            }
    }
}

// Step 10.
private fun recordedNotice(invoice: InvoiceResponse): String {
    val number = invoice.number?.takeIf { invoice.status == READY }
    return "Status aktualisiert: Die Rechnung ist jetzt ${invoiceStatusLabel(invoice.status)}." +
        number?.let { " Vergebene Rechnungsnummer: $it." }.orEmpty()
}

private fun progressRejection(code: String, progress: InvoiceProgressResponse?): OnboardingProblem {
    val current = progress?.let {
        " Aktueller Status: ${invoiceStatusLabel(it.invoice.status)}. " +
            permittedLabel(it.permittedTransitions)
    }
    val cause =
        if (code == "STATE_CHANGED")
            "Die Rechnung wurde inzwischen geändert. Es wurde nichts überschrieben."
        else "Dieser Statuswechsel ist nicht zulässig. Es wurde nichts geändert."
    return OnboardingProblem(cause + current.orEmpty())
}

internal fun permittedLabel(permitted: List<String>) =
    if (permitted.isEmpty()) "Für dich ist kein weiterer Statuswechsel möglich."
    else "Mögliche nächste Status: ${permitted.joinToString(", ") { invoiceStatusLabel(it) }}."

internal fun notReadyProblem() =
    OnboardingProblem(
        "Die Rechnung ist noch nicht bereit zur Ausstellung. Sie bleibt ein Entwurf, und es wurde " +
            "keine Rechnungsnummer vergeben. Die fehlenden Angaben sind unten aufgeführt; die " +
            "Bearbeitung von Entwürfen ist noch nicht verfügbar."
    )

internal fun issuanceNotCompletedProblem() =
    OnboardingProblem(
        "Die Ausstellung wurde nicht abgeschlossen. Die Rechnung bleibt ein Entwurf ohne " +
            "Rechnungsnummer und PDF. Bitte versuche es erneut."
    )

internal fun transitionNotRecordedProblem() =
    OnboardingProblem(
        "Der Statuswechsel wurde nicht erfasst. Status, Rechnungsnummer und Rechnungsangaben " +
            "bleiben unverändert. Bitte versuche es erneut."
    )

internal fun uncertainTransitionProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte lade den Status neu, um den " +
            "gespeicherten Stand zu prüfen."
    )

private fun accessChangedProblem() =
    OnboardingProblem(
        "Dein Zugriff hat sich geändert. Der Status der Rechnung wurde nicht geändert."
    )

private fun readinessMessage(field: String, reason: String) =
    when ("$field/$reason") {
        "settings/MISSING" -> "Die Rechnungseinstellungen der Organisation fehlen."
        "invoiceSequence/MISSING" -> "Für das Rechnungspräfix fehlt der Nummernkreis."
        "invoiceLanguage/UNSUPPORTED" ->
            "Die Rechnungssprache der Organisation wird für das PDF nicht unterstützt."
        "issueDate/MISSING" -> "Das Rechnungsdatum fehlt."
        "issueDate/IN_FUTURE" ->
            "Das Rechnungsdatum liegt in der Zukunft. Die Rechnung kann ab diesem Tag ausgestellt werden."
        "dueDate/MISSING" -> "Das Fälligkeitsdatum fehlt."
        "customerId/INACTIVE" -> "Der Kunde ist nicht mehr aktiv."
        "lines/MISSING" -> "Die Rechnung hat keine Positionen."
        "work/SNAPSHOT_MISSING" -> "Die festgehaltene Arbeit einer Position ist unvollständig."
        else -> "Eine Angabe fehlt oder ist ungültig."
    }

private fun transitionCorrection(reason: String) =
    when (reason) {
        "BEFORE_INVOICE_DATE" -> "Das Datum darf nicht vor dem Rechnungsdatum liegen."
        "AFTER_TODAY" -> "Das Datum darf nicht nach heute liegen."
        "REQUIRED" -> "Bitte gib einen Grund an."
        "TOO_LONG" -> "Höchstens 1000 Zeichen."
        else -> "Gib ein gültiges Datum ein."
    }

internal const val READY = "READY"
internal const val SENT = "SENT"
internal const val PAID = "PAID"
internal const val CANCELLED = "CANCELLED"
